import { useEffect } from "react";
import { useGetSettings } from "@workspace/api-client-react";
import { setJsonLd, setLinkTag, type Restorer } from "@/lib/dom-meta";
import { useI18n } from "@/lib/i18n";

/**
 * Global, site-wide SEO injector mounted once inside StoreLayout.
 *
 * Emits three Schema.org JSON-LD blocks that Google uses for:
 *   - Organization → knowledge-panel & brand entity recognition
 *   - WebSite + SearchAction → site-links searchbox in SERP
 *   - Hreflang alternates (ar/en/x-default) for the current URL
 *
 * Per-page Product / Article / Breadcrumb structured data is injected by the
 * individual pages via usePageMeta + <JsonLd>. This component only provides
 * the site-level baseline that applies to every storefront route.
 */
export function SiteSeo(): null {
  const { data: settings } = useGetSettings();
  const { lang } = useI18n();

  useEffect(() => {
    if (typeof document === "undefined") return;
    const s: any = settings || {};
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const path = typeof window !== "undefined" ? window.location.pathname + window.location.search : "/";
    const url = origin + path;

    // Build per-language URLs so Google can index AR and EN separately.
    // Previously both hreflang tags pointed at the same URL — Google
    // collapsed them as "duplicate content" and never surfaced the
    // English version in en-* SERPs. Adding the `?lang=` param creates
    // two distinct, crawlable URLs while preserving the in-page toggle.
    const urlObj = typeof window !== "undefined" ? new URL(url) : null;
    const buildLangUrl = (l: "ar" | "en"): string => {
      if (!urlObj) return url;
      const u = new URL(urlObj.toString());
      u.searchParams.set("lang", l);
      return u.toString();
    };
    const urlAr = buildLangUrl("ar");
    const urlEn = buildLangUrl("en");

    const storeName: string =
      (typeof s.storeName === "string" && s.storeName.trim()) ||
      "منصة نكسس كارت";
    const storeDescription: string =
      (typeof s.metaDescription === "string" && s.metaDescription.trim()) ||
      (typeof s.storeDescription === "string" && s.storeDescription.trim()) ||
      "";
    const logoUrl: string =
      (typeof s.logoUrl === "string" && s.logoUrl.trim()) ||
      `${origin}/logo-icon.png`;
    const phone: string = (typeof s.contactPhone === "string" && s.contactPhone.trim()) || "";
    const email: string = (typeof s.contactEmail === "string" && s.contactEmail.trim()) || "";
    const addressLine: string = (typeof s.address === "string" && s.address.trim()) || "";
    const countryCode: string = (typeof s.countryCode === "string" && s.countryCode.trim()) || "SA";

    const sameAs: string[] = [];
    for (const key of ["socialFacebook", "socialInstagram", "socialTwitter", "socialTiktok", "socialYoutube", "socialSnapchat", "socialLinkedin"]) {
      const v = s[key];
      if (typeof v === "string" && /^https?:\/\//i.test(v.trim())) sameAs.push(v.trim());
    }

    const organization = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "@id": `${origin}/#organization`,
      name: storeName,
      url: origin + "/",
      logo: logoUrl,
      ...(storeDescription ? { description: storeDescription } : {}),
      ...(sameAs.length ? { sameAs } : {}),
      ...(phone || email
        ? {
            contactPoint: {
              "@type": "ContactPoint",
              contactType: "customer support",
              ...(phone ? { telephone: phone } : {}),
              ...(email ? { email } : {}),
              areaServed: countryCode,
              availableLanguage: ["ar", "en"],
            },
          }
        : {}),
      ...(addressLine
        ? {
            address: {
              "@type": "PostalAddress",
              streetAddress: addressLine,
              addressCountry: countryCode,
            },
          }
        : {}),
    };

    const website = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "@id": `${origin}/#website`,
      url: origin + "/",
      name: storeName,
      inLanguage: lang === "ar" ? "ar-SA" : "en-US",
      publisher: { "@id": `${origin}/#organization` },
      potentialAction: {
        "@type": "SearchAction",
        target: {
          "@type": "EntryPoint",
          urlTemplate: `${origin}/search?q={search_term_string}`,
        },
        "query-input": "required name=search_term_string",
      },
    };

    const cleanups: Restorer[] = [];
    cleanups.push(setJsonLd("site-organization", organization));
    cleanups.push(setJsonLd("site-website", website));
    // hreflang — same URL serves both languages via in-page toggle, so
    // both point to canonical URL. Helps Google understand the page is
    // bilingual and not duplicate content.
    cleanups.push(setLinkTag("alternate", urlAr, { hreflang: "ar" }));
    cleanups.push(setLinkTag("alternate", urlEn, { hreflang: "en" }));
    cleanups.push(setLinkTag("alternate", urlAr, { hreflang: "x-default" }));

    return () => {
      for (const fn of cleanups) {
        try { fn(); } catch { /* noop */ }
      }
    };
  }, [settings, lang]);

  return null;
}
